/**
 * React hook for analytics tracking
 * Provides convenient methods for tracking events in components
 */

import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analytics, { EVENTS } from '../services/analytics';

/**
 * Hook for tracking page views automatically
 * @param {string} pageName - Name of the page
 */
export function usePageView(pageName) {
  const location = useLocation();

  useEffect(() => {
    analytics.trackPageView(pageName, {
      path: location.pathname,
      search: location.search
    });
  }, [pageName, location.pathname, location.search]);
}

/**
 * Hook for tracking element visibility (viewport tracking)
 * @param {string} elementName - Name of the element/section
 * @param {object} options - IntersectionObserver options
 */
export function useViewTracking(elementName, options = {}) {
  const ref = useRef(null);
  const hasTracked = useRef(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasTracked.current) {
            hasTracked.current = true;
            analytics.track('Section Viewed', {
              section_name: elementName,
              visibility_ratio: entry.intersectionRatio
            });
          }
        });
      },
      {
        threshold: options.threshold || 0.5,
        ...options
      }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementName, options]);

  return ref;
}

/**
 * Hook for calculator analytics
 */
export function useCalculatorAnalytics() {
  const trackDimensions = useCallback((width, height, isValid) => {
    analytics.trackDimensionChange(width, height, isValid);
  }, []);

  const trackFinishChange = useCallback((finish) => {
    analytics.track(EVENTS.CALCULATOR_FINISH_CHANGED, { finish });
  }, []);

  const trackColorModeChange = useCallback((mode) => {
    analytics.track(EVENTS.CALCULATOR_COLOR_MODE_CHANGED, { color_mode: mode });
  }, []);

  const trackColorSelected = useCallback((mode, name, value) => {
    analytics.trackColorSelected(mode, name, value);
  }, []);

  const trackRalSearch = useCallback((query, resultsCount) => {
    analytics.track(EVENTS.CALCULATOR_RAL_SEARCHED, {
      query,
      results_count: resultsCount
    });
  }, []);

  const trackBackgroundToneChange = useCallback((tone) => {
    analytics.track(EVENTS.CALCULATOR_BACKGROUND_TONE_CHANGED, { tone });
  }, []);

  const trackImageUpload = useCallback((status, fileSize, fileType) => {
    analytics.trackImageUpload(status, fileSize, fileType);
  }, []);

  const trackImageRemoved = useCallback(() => {
    analytics.track(EVENTS.IMAGE_REMOVED);
  }, []);

  return {
    trackDimensions,
    trackFinishChange,
    trackColorModeChange,
    trackColorSelected,
    trackRalSearch,
    trackBackgroundToneChange,
    trackImageUpload,
    trackImageRemoved
  };
}

/**
 * Hook for delivery analytics
 */
export function useDeliveryAnalytics() {
  const trackCountryChange = useCallback((country, previousCountry) => {
    analytics.track(EVENTS.DELIVERY_COUNTRY_CHANGED, {
      country,
      previous_country: previousCountry
    });
  }, []);

  const trackCarrierChange = useCallback((carrier, country, cost) => {
    analytics.trackDeliverySelection(country, carrier, cost);
  }, []);

  const trackPostalCodeEntered = useCallback((postalCode, country) => {
    analytics.track(EVENTS.DELIVERY_POSTAL_CODE_ENTERED, {
      postal_code_length: postalCode?.length || 0,
      country
    });
  }, []);

  return {
    trackCountryChange,
    trackCarrierChange,
    trackPostalCodeEntered
  };
}

/**
 * Hook for order analytics
 */
export function useOrderAnalytics() {
  const trackOrderButtonClick = useCallback((orderData) => {
    analytics.trackOrderEvent('button_clicked', orderData);
  }, []);

  const trackModalOpen = useCallback(() => {
    analytics.trackOrderEvent('modal_opened');
  }, []);

  const trackModalClose = useCallback(() => {
    analytics.trackOrderEvent('modal_closed');
  }, []);

  const trackFormSubmit = useCallback((formData) => {
    analytics.trackOrderEvent('form_submitted', formData);
  }, []);

  const trackPaymentStarted = useCallback((paymentData) => {
    analytics.trackOrderEvent('payment_started', paymentData);
  }, []);

  const trackOrderComplete = useCallback((orderData) => {
    analytics.trackOrderEvent('completed', orderData);
  }, []);

  const trackOrderFailed = useCallback((error) => {
    analytics.trackOrderEvent('failed', { error_message: error });
  }, []);

  return {
    trackOrderButtonClick,
    trackModalOpen,
    trackModalClose,
    trackFormSubmit,
    trackPaymentStarted,
    trackOrderComplete,
    trackOrderFailed
  };
}

/**
 * Hook for section visibility tracking
 */
export function useSectionAnalytics() {
  const trackSectionToggle = useCallback((sectionName, isExpanded) => {
    analytics.trackSectionToggle(sectionName, isExpanded);
  }, []);

  return { trackSectionToggle };
}

/**
 * Hook for navigation analytics
 */
export function useNavigationAnalytics() {
  const trackLinkClick = useCallback((linkName, destination) => {
    analytics.track(EVENTS.NAV_LINK_CLICKED, {
      link_name: linkName,
      destination
    });
  }, []);

  const trackLanguageChange = useCallback((newLanguage, previousLanguage) => {
    analytics.track(EVENTS.LANGUAGE_CHANGED, {
      new_language: newLanguage,
      previous_language: previousLanguage
    });
    analytics.setUserProperties({ language: newLanguage });
  }, []);

  return {
    trackLinkClick,
    trackLanguageChange
  };
}

/**
 * Hook for gallery analytics
 */
export function useGalleryAnalytics() {
  const trackImageViewed = useCallback((imageId, imageName) => {
    analytics.track(EVENTS.GALLERY_IMAGE_VIEWED, {
      image_id: imageId,
      image_name: imageName
    });
  }, []);

  const trackImageClicked = useCallback((imageId, imageName) => {
    analytics.track(EVENTS.GALLERY_IMAGE_CLICKED, {
      image_id: imageId,
      image_name: imageName
    });
  }, []);

  return {
    trackImageViewed,
    trackImageClicked
  };
}

/**
 * Hook for error tracking
 */
export function useErrorAnalytics() {
  const trackError = useCallback((errorType, errorMessage, context) => {
    analytics.trackError(errorType, errorMessage, context);
  }, []);

  const trackValidationError = useCallback((field, message) => {
    analytics.track(EVENTS.VALIDATION_ERROR, {
      field,
      message
    });
  }, []);

  return {
    trackError,
    trackValidationError
  };
}

export default analytics;
